import React from 'react';
import { MaterialResumo } from './types';

interface MarkdownProps {
  conteudo: string;
  origem?: string;
  materiais?: MaterialResumo[];
  abrirMaterial?: (material: MaterialResumo) => void;
}

function limparCaminho(caminho: string) {
  let valor = caminho;
  try { valor = new URL(caminho).pathname; } catch { /* caminho relativo */ }
  try { valor = decodeURIComponent(valor); } catch { /* mantém o texto original se a codificação estiver quebrada */ }
  return valor.split('#')[0].split('?')[0].replace(/^\.\//, '').replace(/\\/g, '/').toLowerCase();
}

function resolverLink(alvo: string, origem?: string): string | null {
  if (/^[a-z][a-z0-9+.-]*:/i.test(alvo) && !/^(https?:|mailto:)/i.test(alvo)) return null;
  if (/^mailto:/i.test(alvo)) return alvo;
  try {
    if (origem && /^https?:\/\//i.test(origem)) return new URL(alvo, origem).toString();
    const caminhoOrigem = (origem || '').replace(/\\/g, '/').replace(/^\/+/, '');
    return new URL(alvo, `https://github.com/hydrodataeng/nexo/blob/main/${caminhoOrigem}`).toString();
  } catch { return null; }
}

function partesInline(texto: string, props: MarkdownProps): React.ReactNode[] {
  const partes: React.ReactNode[] = [];
  const regex = /(`[^`]+`|\*\*[^*]+\*\*|\[[^\]]+\]\([^)]+\))/g;
  let inicio = 0;
  let resultado: RegExpExecArray | null;

  while ((resultado = regex.exec(texto)) !== null) {
    if (resultado.index > inicio) partes.push(texto.slice(inicio, resultado.index));
    const trecho = resultado[0];
    if (trecho.startsWith('`')) {
      partes.push(<code key={resultado.index}>{trecho.slice(1, -1)}</code>);
    } else if (trecho.startsWith('**')) {
      partes.push(<strong key={resultado.index}>{trecho.slice(2, -2)}</strong>);
    } else {
      const link = /^\[([^\]]+)\]\(([^)]+)\)$/.exec(trecho);
      if (link) {
        const hrefResolvido = resolverLink(link[2], props.origem);
        const alvo = limparCaminho(hrefResolvido || link[2]);
        const nomeAlvo = alvo.split('/').pop() || alvo;
        const material = props.materiais?.find(item => {
          const origem = limparCaminho(item.origem);
          return origem === alvo || origem.endsWith(`/${alvo}`) || origem.split('/').pop() === nomeAlvo || item.id.toLowerCase() === nomeAlvo.replace(/\.md$/, '');
        });
        if (material && props.abrirMaterial) {
          partes.push(<a key={resultado.index} href={`?aba=materiais&material=${encodeURIComponent(material.id)}`}
            onClick={evento => { evento.preventDefault(); props.abrirMaterial?.(material); }}>{link[1]}</a>);
        } else if (hrefResolvido && /^(https?:\/\/|mailto:)/i.test(hrefResolvido)) {
          partes.push(<a key={resultado.index} href={hrefResolvido} target="_blank" rel="noreferrer">{link[1]}</a>);
        } else {
          partes.push(link[1]);
        }
      } else {
        partes.push(trecho);
      }
    }
    inicio = resultado.index + trecho.length;
  }
  if (inicio < texto.length) partes.push(texto.slice(inicio));
  return partes;
}

function celulasTabela(linha: string) {
  return linha.trim().replace(/^\||\|$/g, '').split('|').map(celula => celula.trim());
}

export default function MarkdownSeguro(props: MarkdownProps) {
  const { conteudo } = props;
  const linhas = (conteudo || '').replace(/\r\n/g, '\n').split('\n');
  const blocos: React.ReactNode[] = [];
  let indice = 0;

  while (indice < linhas.length) {
    const linha = linhas[indice];
    if (!linha.trim()) { indice += 1; continue; }

    if (linha.trim().startsWith('```')) {
      const codigo: string[] = [];
      indice += 1;
      while (indice < linhas.length && !linhas[indice].trim().startsWith('```')) {
        codigo.push(linhas[indice]);
        indice += 1;
      }
      indice += 1;
      blocos.push(<pre key={`pre-${indice}`}><code>{codigo.join('\n')}</code></pre>);
      continue;
    }

    const cabecalho = /^(#{1,4})\s+(.+)$/.exec(linha);
    if (cabecalho) {
      const nivel = cabecalho[1].length;
      const filhos = partesInline(cabecalho[2], props);
      blocos.push(React.createElement(`h${nivel}`, { key: `h-${indice}` }, filhos));
      indice += 1;
      continue;
    }

    if (/^\s*[-*]\s+/.test(linha)) {
      const itens: React.ReactNode[] = [];
      while (indice < linhas.length && /^\s*[-*]\s+/.test(linhas[indice])) {
        const chave = indice;
        const texto = [linhas[indice].replace(/^\s*[-*]\s+/, '')];
        indice += 1;
        while (indice < linhas.length && /^\s{2,}\S/.test(linhas[indice]) && !/^\s*([-*]|\d+\.)\s+/.test(linhas[indice])) {
          texto.push(linhas[indice].trim()); indice += 1;
        }
        itens.push(<li key={chave}>{partesInline(texto.join(' '), props)}</li>);
      }
      blocos.push(<ul key={`ul-${indice}`}>{itens}</ul>);
      continue;
    }

    if (/^\s*\d+\.\s+/.test(linha)) {
      const itens: React.ReactNode[] = [];
      const primeiroNumero = Number(/^\s*(\d+)/.exec(linha)?.[1] || 1);
      while (indice < linhas.length && /^\s*\d+\.\s+/.test(linhas[indice])) {
        const chave = indice;
        const texto = [linhas[indice].replace(/^\s*\d+\.\s+/, '')];
        indice += 1;
        while (indice < linhas.length && /^\s{2,}\S/.test(linhas[indice]) && !/^\s*([-*]|\d+\.)\s+/.test(linhas[indice])) {
          texto.push(linhas[indice].trim()); indice += 1;
        }
        itens.push(<li key={chave}>{partesInline(texto.join(' '), props)}</li>);
      }
      blocos.push(<ol key={`ol-${indice}`} start={primeiroNumero}>{itens}</ol>);
      continue;
    }

    if (linha.includes('|') && indice + 1 < linhas.length && /^\s*\|?\s*:?-{3,}/.test(linhas[indice + 1])) {
      const cabecalhos = celulasTabela(linha);
      indice += 2;
      const linhasTabela: string[][] = [];
      while (indice < linhas.length && linhas[indice].includes('|') && linhas[indice].trim()) {
        linhasTabela.push(celulasTabela(linhas[indice]));
        indice += 1;
      }
      blocos.push(<div className="nexo-table-scroll" key={`table-${indice}`}><table><thead><tr>{cabecalhos.map((celula, i) => <th key={i}>{partesInline(celula, props)}</th>)}</tr></thead>
        <tbody>{linhasTabela.map((celulas, i) => <tr key={i}>{cabecalhos.map((_, j) => <td key={j}>{partesInline(celulas[j] || '', props)}</td>)}</tr>)}</tbody></table></div>);
      continue;
    }

    const paragrafo = [linha.trim()];
    indice += 1;
    while (indice < linhas.length && linhas[indice].trim() &&
      !/^(#{1,4})\s+|^\s*[-*]\s+|^\s*\d+\.\s+|^\s*```/.test(linhas[indice])) {
      paragrafo.push(linhas[indice].trim());
      indice += 1;
    }
    blocos.push(<p key={`p-${indice}`}>{partesInline(paragrafo.join(' '), props)}</p>);
  }

  return <div className="nexo-markdown">{blocos}</div>;
}
